import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as ContextMenuIcon } from '../assets/svg/icon-more-options.svg';
import { ReactComponent as ToggleRowIcon } from '../assets/svg/icon-dropdown-arrow.svg';

const GroupedDataTable = ({
  groupedData,
  headers,
  groupTitleFn,
  rowKeyFn,
  onPrimaryAction,
  contextualOptionsFn,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 700);
  const menuRef = useRef();

  const toggleMenu = (e, id) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === id ? null : id);
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

useEffect(() => {
  const handleResize = () => {
    const nowMobile = window.innerWidth < 700;
    setIsMobile(nowMobile);

    if (!nowMobile) {
      // Clean up any open mobile rows when switching to desktop
      document.querySelectorAll(".table-list__mobile-data-container").forEach((row) => {
        row.style.display = "none";
        row.previousElementSibling?.classList.remove("table-list__selected-mobile");
      });
    }
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  const hasContextualOptions = typeof contextualOptionsFn === "function" &&
    Object.values(groupedData).some(group =>
      group.some(item => contextualOptionsFn(item)?.length)
    );

  return (
    <div>
      <table className="table-list">
        <tbody>
          {Object.entries(groupedData).map(([groupName, items], groupIndex) => (
            <React.Fragment key={groupName}>
              <tr className="table-list__section">
                <td colSpan={isMobile ? 1 : headers.length + (hasContextualOptions ? 1 : 0)}>
                  {groupTitleFn(groupName)}
                </td>
              </tr>

              {groupIndex === 0 && (
                <tr className="table-list__header-row">
                  {headers.map((header, i) => (
                    <td
                      key={i}
                      className={[
                        i === 0 ? "table-list__header-cell-first" : "",
                        header.hideMobile ? "table-list__hide-mobile" : "",
                        header.alignRight ? "table-list__align-right" : "",
                      ].join(" ")}
                    >
                      {header.label}
                    </td>
                  ))}
                  {hasContextualOptions && (
                    <td className="table-list__hide-mobile table-list__align-right"></td>
                  )}
                </tr>
              )}

              {items.map((item) => {
                const key = rowKeyFn(item);

                return (
                  <React.Fragment key={key}>
                    <tr
                      className="table-list__machine-row"
                      onClick={(e) => {
                        if (isMobile) {
                          const row = e.currentTarget;
                          const next = row.nextElementSibling;
                          if (next) {
                            const isVisible = next.style.display === "table-row";
                            next.style.display = isVisible ? "none" : "table-row";
                            row.classList.toggle("table-list__selected-mobile", !isVisible);
                          }
                        } else {
                          onPrimaryAction(item);
                        }
                      }}
                    >
                      {headers.map((header, i) => (
                        <td
                          key={i}
                          className={[
                            i === 0 ? "table-list__expand-row" : "",
                            i === 0 ? "color-block-grey" : "",
                            header.hideMobile ? "table-list__hide-mobile" : "",
                            header.alignRight ? "table-list__align-right" : "",
                          ].join(" ")}
                        >
                          {i === 0 && (
                            <>
                              <div className="table-list__toggle-icon"><ToggleRowIcon /></div>
                              <a href="#" className="table-list__primary-btn">
                                {header.primaryLabel || "Edit"}
                              </a>
                            </>
                          )}
                          {header.render
                            ? header.render(item)
                            : item[header.accessor] || "-"}
                        </td>
                      ))}

                      {hasContextualOptions && (
                        <td
                          className="table-list__hide-mobile table-list__options table-list__align-right"
                          onClick={(e) => toggleMenu(e, key)}
                        >
                          <div className="table-list__options-icon"><ContextMenuIcon /></div>
                          {openMenuId === key && (
                            <div className="table-list__context-menu" ref={menuRef}> 
                              {contextualOptionsFn(item).map((opt, idx) => (
                                <Link
                                  key={idx}
                                  to={opt.href}
                                  className="table-list__context-menu-option"
                                  onClick={opt.onClick}
                                >
                                  {opt.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                    <tr
                      className="table-list__mobile-data-container"
                      style={{ display: isMobile ? "none" : "none" }} // default hidden
                    >
                      {isMobile && (
                        <td colSpan={headers.length + (hasContextualOptions ? 1 : 0)}>
                          <table className="table-list__mobile-data-table">
                            {headers
                              .filter((_, i) => i !== 0)
                              .map((header, i) => (
                                <tr key={i}>
                                  <td>{header.label}</td>
                                  <td>
                                    {header.render
                                      ? header.render(item)
                                      : item[header.accessor] || "-"}
                                  </td>
                                </tr>
                              ))}
                          </table>
                          <div className="table-list__mobile-data-container-options">
                            <button
                              type="button"
                              className="button--blue"
                              onClick={() => onPrimaryAction(item)}
                            >
                              Edit
                            </button>
                            {hasContextualOptions && (
                              <>
                                <button
                                  type="button"
                                  className="slim-button more-options"
                                  onClick={(e) => toggleMenu(e, rowKeyFn(item))}
                                >
                                  <ContextMenuIcon />
                                </button>
                                {openMenuId === rowKeyFn(item) && (
                                  <div className="table-list__context-menu" ref={menuRef}>
                                    {contextualOptionsFn(item).map((opt, idx) => (
                                      <Link
                                        key={idx}
                                        to={opt.href}
                                        className="table-list__context-menu-option"
                                        onClick={opt.onClick}
                                      >
                                        {opt.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GroupedDataTable;
